import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, CalendarDays, BookOpen, FolderTree, Settings as SettingsIcon, Mail, LogOut, Star, Image as ImageIcon, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Logo, LogoIcon } from '@/components/Logo';

const ALL_LINKS = [
  { to: '/admin', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/admin/reservations', icon: CalendarDays, label: 'Reservations' },
  { to: '/admin/menu', icon: BookOpen, label: 'Menu Items' },
  { to: '/admin/categories', icon: FolderTree, label: 'Categories' },
  { to: '/admin/taste', icon: ImageIcon, label: 'A Taste of This Place' },
  { to: '/admin/reviews', icon: Star, label: 'Reviews' },
  { to: '/admin/settings', icon: SettingsIcon, label: 'Settings' },
  { to: '/admin/users', icon: ShieldCheck, label: 'Users', superOnly: true },
  { to: '/admin/email-log', icon: Mail, label: 'Email Log', superOnly: true },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = () => { logout(); navigate('/admin/login'); };
  const LINKS = ALL_LINKS.filter((l) => !l.superOnly || user?.role === 'super_admin');
  return (
    <div className="min-h-screen bg-[hsl(var(--sb-bg))] flex">
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-[hsl(var(--sb-border))] bg-[hsl(var(--sb-surface))] sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-3 px-5 py-5 border-b border-[hsl(var(--sb-border))]">
          <LogoIcon size={44} />
          <div>
            <div className="font-display text-lg leading-none text-white">SMITHY BROWNS</div>
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--sb-gold))]">Admin Console</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? 'bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))]' : 'text-white/75 hover:text-white hover:bg-white/5'}`}
            >
              <l.icon size={18} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[hsl(var(--sb-border))]">
          <div className="text-xs text-white/60 mb-2">Signed in as</div>
          <div className="text-sm text-white truncate">{user?.email}</div>
          <button onClick={onLogout} className="mt-3 w-full inline-flex items-center justify-center gap-2 h-9 rounded-md border border-[hsl(var(--sb-border))] text-white text-sm hover:bg-white/5">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[hsl(var(--sb-bg))]/85 backdrop-blur border-b border-[hsl(var(--sb-border))]">
          <div className="sb-flag-stripes" />
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <div className="text-white/70 text-sm hidden md:block">Smithy Browns · Restaurant Admin</div>
            <Link to="/" className="text-xs uppercase tracking-widest text-[hsl(var(--sb-gold))] hover:text-white">View site →</Link>
          </div>
        </header>
        {/* Mobile nav */}
        <nav className="md:hidden border-b border-[hsl(var(--sb-border))] bg-[hsl(var(--sb-surface))] overflow-x-auto no-scrollbar">
          <ul className="flex gap-1 px-2 py-2 min-w-max">
            {LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) => `inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs whitespace-nowrap transition-colors ${isActive ? 'bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))]' : 'text-white/80'}`}
                >
                  <l.icon size={14} /> {l.label}
                </NavLink>
              </li>
            ))}
            <li>
              <button onClick={onLogout} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs text-white/70">
                <LogOut size={14} /> Logout
              </button>
            </li>
          </ul>
        </nav>
        <div className="flex-1 p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
};
